import {
  useCallback, useEffect, useLayoutEffect, useRef,
} from 'react';
import {
  Container, ISpritePointerEvent, Root, addLicense, color,
} from '@amcharts/amcharts5';
import am5geodata_brazilLow from '@amcharts/amcharts5-geodata/brazilLow';
import {
  MapChart, MapPointSeries, MapPolygonSeries, ZoomControl, ClusteredPointSeries, geoNaturalEarth1,
} from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import {
  buildCustomButtonStyles,
  customGroupBullet, unitMarker, customMinusIcon, customPlusIcon, customStateBullet,
  getResponsiveTheme,
  backButton,
} from '../components/MapChart/components';
import { mapColors } from '../colors';
import * as helpers from '../helpers';
import { DataContextUnitMarker, MapUnitInfo } from '../types';
import { defaultChartSettings, splittedPointSettings } from '../constants';
import { UnitMap } from '../MapContent';

type Event<T> = ISpritePointerEvent & {
  type: T;
  target: Container;
};

interface UseMapResponse {
  goCountryMap: () => void;
  updateCountryMap: (map: UnitMap) => void;
  updateStateMap: (newState: string, map: UnitMap) => void;
  onCloseUnitCard: () => void;
}

interface UseMapProps {
  stateEvents: {
    onClick: (newState: string) => void;
    onPointerOver: (newState: string) => void;
    onPointerOut: () => void;
  };
  unitEvents: {
    onClick: (unitId: string, info: MapUnitInfo) => void;
    onPointerOver: (unitId: string, info: MapUnitInfo) => void;
    onPointerOut: (unitId: string, info: MapUnitInfo) => void;
  };
  disabled: boolean;
  onClickResetButton: () => void;
}

interface MapReferences {
  chart: MapChart;
  mapPointSeries: MapPointSeries;
  countryPolygonSeries: MapPolygonSeries;
  statePolygonSeries: MapPolygonSeries;
  zoomControl: ZoomControl;
  clusteredPointSeries: ClusteredPointSeries;
  root: Root;
  backButton: Container;
}

export function useMap({
  stateEvents,
  unitEvents,
  disabled,
  onClickResetButton,
}: UseMapProps): UseMapResponse {
  const mapReferences = useRef<MapReferences| undefined>(undefined);

  function onClickStateMarker(
    event: ISpritePointerEvent & {
            type: 'click';
            target: Container;
        },
  ): void {
    if (event.target.dataItem) {
      const newState = helpers.getStateNameByPolygon(event);
      stateEvents.onClick(newState);
    }
  }

  function onClickUnitMarker(
    event: ISpritePointerEvent & {
            type: 'click';
            target: Container;
        },
  ): void {
    if (event.target.dataItem) {
      const data = event.target.dataItem.dataContext as DataContextUnitMarker;
      unitEvents.onClick(data.unitId, {
        state: data.state,
        name: data.title,
        city: data.city,
        color: data.color,
        id: data.unitId,
        latitude: data.lat,
        longitude: data.lon,
      });
      helpers.onClickUnitMarker(event);
    }
  }

  function onPointerOverStateMarker(event): void {
    const newState = helpers.getStateNameByPolygon(event);
    stateEvents.onPointerOver(newState);
    helpers.onPointerOverStateMarker(event);
  }

  function onPointerOutStateMarker(event): void {
    stateEvents.onPointerOut();
    helpers.onPointerOutStateMarker(event);
  }

  function onPointerOverUnitMarker(event: Event<'pointerover'>): void {
    if (event.target.dataItem) {
      const data = event.target.dataItem.dataContext as DataContextUnitMarker;
      unitEvents.onPointerOver(data.unitId, {
        state: data.state,
        name: data.title,
        city: data.city,
        color: data.color,
        id: data.unitId,
        latitude: data.lat,
        longitude: data.lon,
      });
    }
  }

  function onPointerOutUnitMarker(event: Event<'pointerout'>): void {
    if (event.target.dataItem) {
      const data = event.target.dataItem.dataContext as DataContextUnitMarker;
      unitEvents.onPointerOut(data.unitId, {
        state: data.state,
        name: data.title,
        city: data.city,
        color: data.color,
        id: data.unitId,
        latitude: data.lat,
        longitude: data.lon,
      });
    }
  }

  function updateStateMap(newState: string, map: UnitMap): void {
    const polygonId = `BR-${newState}`;
    if (!mapReferences.current) return;

    if (mapReferences.current.countryPolygonSeries.isVisible()) {
      mapReferences.current.mapPointSeries.hide(100);
      mapReferences.current.countryPolygonSeries.hide(100);
    }

    mapReferences.current.clusteredPointSeries.data.clear();
    mapReferences.current.statePolygonSeries.data.clear();

    mapReferences.current?.chart.set('minZoomLevel', 2.5);

    helpers.buildStateMap(
      polygonId,
      mapReferences.current.chart,
      mapReferences.current.statePolygonSeries,
      mapReferences.current.zoomControl,
      mapReferences.current.backButton,
    );
    mapReferences.current.statePolygonSeries.show(100);

    const units = map.get(newState) ?? [];
    const parsedUnits = units.map(helpers.parseMapInfo);
    helpers.buildStateMapMarkers(
      mapReferences.current.clusteredPointSeries,
      parsedUnits,
    );

    mapReferences.current.clusteredPointSeries.show(100);
  }

  const disabledMap = useCallback((): void => {
    if (mapReferences.current && disabled) {
      mapReferences.current.mapPointSeries.bulletsContainer.children.clear();
      mapReferences.current.mapPointSeries.bullets.clear();

      mapReferences.current.mapPointSeries.bullets.push(
        (root, _, dataItem) => customStateBullet(root, dataItem, undefined, true),
      );
      helpers.buildCountryMapMarkers(mapReferences.current.root, mapReferences.current.mapPointSeries, new Map(), true);
    }
  }, [disabled]);

  // Reconfigura bullets e interações quando o mapa volta a estar habilitado
  const enableMap = useCallback((): void => {
    if (!mapReferences.current) return;
    const ps = mapReferences.current.mapPointSeries;
    ps.bulletsContainer.children.clear();
    ps.bullets.clear();
    ps.bullets.push((root, _, dataItem) => customStateBullet(root, dataItem, {
      onClick: onClickStateMarker,
      onPointerOver: onPointerOverStateMarker,
      onPointerOut: onPointerOutStateMarker,
    }));
  }, []);

  const updateCountryMap = useCallback((map: UnitMap): void => {
    if (mapReferences.current) {
      mapReferences.current.mapPointSeries.data.clear();
      mapReferences.current.mapPointSeries.hide(100);

      const mapToRender = map && map.size > 0 ? map : new Map();
      const parsedMap = helpers.parseMapValues(mapToRender);

      helpers.buildCountryMapMarkers(
        mapReferences.current.root,
        mapReferences.current.mapPointSeries,
        parsedMap,
        disabled,
      );

      mapReferences.current.mapPointSeries.show(100);
    }

    if (disabled) {
      disabledMap();
    }
  }, [disabled, disabledMap]);

  function goCountryMap(): void {
    if (mapReferences.current) {
      mapReferences.current.zoomControl.hide(100);
      mapReferences.current.statePolygonSeries.hide(100);
      mapReferences.current.clusteredPointSeries.hide(100);
      mapReferences.current.mapPointSeries.show(100);
      mapReferences.current.countryPolygonSeries.show(100);
      mapReferences.current.chart.set('minZoomLevel', 0);
      mapReferences.current.chart.setAll(defaultChartSettings);
      mapReferences.current.backButton.hide(100);
      mapReferences.current.chart.goHome();
    }
  }

  function onCloseUnitCard(): void {
    if (mapReferences.current) {
      const container = mapReferences.current.clusteredPointSeries.children;
      const [containerParent] = container.values;
      const containerMarkers = (containerParent as Container).children.values;

      for (const marker of containerMarkers) {
        const containerMarker = marker as Container;
        for (const childMarker of containerMarker.children.values) {
          childMarker.states.apply('default');
        }
      }
    }
  }

  function onClickBackButton(): void {
    goCountryMap();
    onClickResetButton();
  }

  useLayoutEffect(() => {
    if (process.env.REACT_APP_AMCHARTS_LICENSE) {
      addLicense(process.env.REACT_APP_AMCHARTS_LICENSE);
    }
    const root = Root.new('map-chart');
    const responsiveTheme = getResponsiveTheme(root);

    root.setThemes([
      am5themes_Animated.new(root),
      responsiveTheme,
    ]);

    // Chart
    const chart = root.container.children.push(
      MapChart.new(root, {
        projection: geoNaturalEarth1(),
        ...defaultChartSettings,
      }),
    );

    // Zoom Control
    const zoomControl = chart.set(
      'zoomControl',
      ZoomControl.new(root, {
        visible: false,
      }),
    );

    buildCustomButtonStyles(zoomControl.plusButton);
    buildCustomButtonStyles(zoomControl.minusButton);
    zoomControl.plusButton.set('icon', customPlusIcon(root));
    zoomControl.minusButton.set('icon', customMinusIcon(root));

    // Map Brazil
    const polygonSeries = chart.series.push(
      MapPolygonSeries.new(root, {
        geoJSON: am5geodata_brazilLow,
        fill: color(mapColors.background),
        stroke: color(mapColors.foreground),
      }),
    );

    // State Map
    const statePolygonSeries = chart.series.push(
      MapPolygonSeries.new(root, {
        visible: false,
        fill: color(mapColors.background),
        stroke: color(mapColors.foreground),
      }),
    );

    const mapPointSeries = chart.series.push(
      MapPointSeries.new(root, {
        autoScale: true,
        affectsBounds: false,
      }),
    );

    // Grouped state points
    const clusteredStatePointSeries = chart.series.push(
      ClusteredPointSeries.new(root, {
        minDistance: 20,
        affectsBounds: false,
        scatterRadius: 0,
        scatterDistance: 0,
        visible: false,
        ...splittedPointSettings,
      }),
    );
    clusteredStatePointSeries.set('clusteredBullet', customGroupBullet);
    clusteredStatePointSeries.bullets.push((root, _, dataItem) => unitMarker(root, dataItem, {
      onClick: onClickUnitMarker,
      onPointerOut: onPointerOutUnitMarker,
      onPointerOver: onPointerOverUnitMarker,
    }));

    // State bullets
    if (disabled) {
      disabledMap();
    } else {
      enableMap();
    }

    // Back button
    const mapBackButton = chart.children.push(backButton(root, onClickBackButton));

    polygonSeries.mapPolygons.template.setAll({
      strokeWidth: 0.5,
      templateField: 'polygonSettings',
    });

    statePolygonSeries.mapPolygons.template.setAll({
      strokeWidth: 0.5,
    });

    mapReferences.current = {
      chart,
      clusteredPointSeries: clusteredStatePointSeries,
      countryPolygonSeries: polygonSeries,
      mapPointSeries,
      statePolygonSeries,
      zoomControl,
      root,
      backButton: mapBackButton,
    };

    return () => {
      root.dispose();
      mapReferences.current = undefined;
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      disabledMap();
    } else {
      enableMap();
    }
  }, [disabled]);

  return {
    updateStateMap,
    updateCountryMap,
    goCountryMap,
    onCloseUnitCard,
  };
}
